import { getSessionSelectedAccount } from "~/domains/user/sessionRuntime";

export const QUOTE_SEARCH_QUERY_KEYS = {
  queryType: "quoteQueryType",
  dateShortcut: "quoteDateShortcut",
  dateFrom: "quoteDateFrom",
  dateTo: "quoteDateTo",
  status: "quoteStatus",
  lineStatus: "quoteLineStatus",
} as const;

/** Raw query values from the pre-URL-state implementation are private identifiers. */
export const LEGACY_QUOTE_SEARCH_QUERY_KEY = "quoteQuery";

export interface QuoteSearchAccountContext {
  accountNumber: string;
  endCustomer: string;
  salesOrg: string;
}

export function getCurrentQuoteSearchAccount(): QuoteSearchAccountContext | undefined {
  const selectedAccount = getSessionSelectedAccount();
  if (!selectedAccount?.uid || !selectedAccount.salesOrg) {
    return undefined;
  }

  return {
    accountNumber: selectedAccount.uid,
    endCustomer: selectedAccount.endCustomerCode ?? "",
    salesOrg: selectedAccount.salesOrg,
  };
}

/**
 * Account switching can redirect back to the page that initiated the switch.
 * Quote filters are public URL state, but they are not valid across account
 * contexts, so strip only this page's committed query before following it.
 */
export function removeQuoteSearchUrlState(url: string): string {
  return removeQuoteSearchQueryParameters(url, [
    ...Object.values(QUOTE_SEARCH_QUERY_KEYS),
    LEGACY_QUOTE_SEARCH_QUERY_KEY,
  ]);
}

/**
 * Account switching can invalidate the committed quote-search query this page owns. Raw legacy
 * `quoteQuery` values are private identifiers, so strip that key before navigating.
 */
export function removeLegacyQuoteSearchQuery(url: string): string {
  return removeQuoteSearchQueryParameters(url, [LEGACY_QUOTE_SEARCH_QUERY_KEY]);
}

function removeQuoteSearchQueryParameters(url: string, keys: string[]): string {
  const parsedUrl = new URL(url, window.location.origin);
  keys.forEach((key) => parsedUrl.searchParams.delete(key));

  const isAbsoluteUrl = /^[a-z][a-z\d+.-]*:/i.test(url);
  return isAbsoluteUrl ? parsedUrl.toString() : `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
}
